import { useCallback, useEffect } from 'react';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';

/**
 * @memberOf Playtech.API.events
 * @event sharedPagesEvents
 * @type {callback}
 * @example Playtech.on('player-changes-tab', (details) => { ... });
 * @example Playtech.on('player-clicks-join-mp', (details) => { ... });
 * @example Playtech.on('player-clicks-join-lb', (details) => { ... });
 * @example Playtech.on('player-clicks-close', (details) => { ... });
 * @example Playtech.on('player-changes-game', (details) => { ... });
 * @example Playtech.on('player-clicks-leave-mp', (details) => { ... });
 * @example Playtech.on('player-clicks-leave-lb', (details) => { ... });
 * @example Playtech.on('player-scrolls-down', (details) => { ... });
 * @example Playtech.on('promotion-opened', (details) => { ... });
 * @example Playtech.on('section-expanded', (details) => { ... });
 * @example Playtech.on('section-collapsed', (details) => { ... });
 * @example Playtech.on('section-expanded', (details) => { ... });
 * @example Playtech.on('player-clicks-backbutton', (details) => { ... });
 */
export const PLAYER_CHANGES_TAB = 'player-changes-tab';
export const PLAYER_CLICKS_JOIN_MP = 'player-clicks-join-mp';
export const PLAYER_CLICKS_JOIN_LB = 'player-clicks-join-lb';
export const PLAYER_CLICKS_CLOSE = 'player-clicks-close';
export const PLAYER_CHANGES_GAME = 'player-changes-game';
export const PLAYER_CLICKS_LEAVE_MP = 'player-clicks-leave-mp';
export const PLAYER_CLICKS_LEAVE_LB = 'player-clicks-leave-lb';
export const PLAYER_SCROLLS_DOWN = 'player-scrolls-down';
export const PLAYER_PROMOTION_OPENED = 'promotion-opened';
export const PLAYER_SECTION_EXPANDED = 'section-expanded';
export const PLAYER_SECTION_COLLAPSED = 'section-collapsed';
export const PLAYER_CLICKS_BACKBUTTON = 'player-clicks-backbutton';

export const useOnHub2SharedPagesEventsTrack = (eventTarget) => {
    const playerChangedTabEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CHANGES_TAB, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerClicksJoinMpEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CLICKS_JOIN_MP, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerPromotionOpened = useCallback((info) => {
        const event = new CustomEvent(PLAYER_PROMOTION_OPENED, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerSectionExpanded = useCallback((info) => {
        const event = new CustomEvent(PLAYER_SECTION_EXPANDED, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerSectionCollapsed = useCallback((info) => {
        const event = new CustomEvent(PLAYER_SECTION_COLLAPSED, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerClicksBackButton = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CLICKS_BACKBUTTON, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerClicksJoinLbEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CLICKS_JOIN_LB, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerClicksCloseEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CLICKS_CLOSE, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerChangesGameEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CHANGES_GAME, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerClicksLeaveMpEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CLICKS_LEAVE_MP, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerClicksLeaveLbEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_CLICKS_LEAVE_LB, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);
    const playerScrollsDownEvent = useCallback((info) => {
        const event = new CustomEvent(PLAYER_SCROLLS_DOWN, { detail: { ...info } });
        eventTarget.dispatchEvent(event);
    }, [eventTarget]);

    useEffect(() => {
        publicEventsAPIService.on(PLAYER_CHANGES_TAB, playerChangedTabEvent);
        publicEventsAPIService.on(PLAYER_CLICKS_JOIN_MP, playerClicksJoinMpEvent);
        publicEventsAPIService.on(PLAYER_CLICKS_JOIN_LB, playerClicksJoinLbEvent);
        publicEventsAPIService.on(PLAYER_CLICKS_CLOSE, playerClicksCloseEvent);
        publicEventsAPIService.on(PLAYER_CHANGES_GAME, playerChangesGameEvent);
        publicEventsAPIService.on(PLAYER_CLICKS_LEAVE_MP, playerClicksLeaveMpEvent);
        publicEventsAPIService.on(PLAYER_CLICKS_LEAVE_LB, playerClicksLeaveLbEvent);
        publicEventsAPIService.on(PLAYER_SCROLLS_DOWN, playerScrollsDownEvent);
        publicEventsAPIService.on(PLAYER_PROMOTION_OPENED, playerPromotionOpened);
        publicEventsAPIService.on(PLAYER_SECTION_EXPANDED, playerSectionExpanded);
        publicEventsAPIService.on(PLAYER_SECTION_COLLAPSED, playerSectionCollapsed);
        publicEventsAPIService.on(PLAYER_CLICKS_BACKBUTTON, playerClicksBackButton);

        return () => {
            publicEventsAPIService.off(PLAYER_CHANGES_TAB, playerChangedTabEvent);
            publicEventsAPIService.off(PLAYER_CLICKS_JOIN_MP, playerClicksJoinMpEvent);
            publicEventsAPIService.off(PLAYER_CLICKS_JOIN_LB, playerClicksJoinLbEvent);
            publicEventsAPIService.off(PLAYER_CLICKS_CLOSE, playerClicksCloseEvent);
            publicEventsAPIService.off(PLAYER_CHANGES_GAME, playerChangesGameEvent);
            publicEventsAPIService.off(PLAYER_CLICKS_LEAVE_MP, playerClicksLeaveMpEvent);
            publicEventsAPIService.off(PLAYER_CLICKS_LEAVE_LB, playerClicksLeaveLbEvent);
            publicEventsAPIService.off(PLAYER_SCROLLS_DOWN, playerScrollsDownEvent);
            publicEventsAPIService.off(PLAYER_PROMOTION_OPENED, playerPromotionOpened);
            publicEventsAPIService.off(PLAYER_SECTION_EXPANDED, playerSectionExpanded);
            publicEventsAPIService.off(PLAYER_SECTION_COLLAPSED, playerSectionCollapsed);
            publicEventsAPIService.off(PLAYER_CLICKS_BACKBUTTON, playerClicksBackButton);
        };
    }, [
        playerChangedTabEvent, playerClicksJoinMpEvent, playerClicksCloseEvent, playerChangesGameEvent,
        playerClicksLeaveMpEvent, playerScrollsDownEvent, playerClicksLeaveLbEvent, playerClicksJoinLbEvent,
        playerClicksBackButton, playerSectionCollapsed, playerSectionExpanded, playerPromotionOpened
    ]);
};
